import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput10 from "@/components/site/PhoneInput10";
import DoctorNameInput, { stripDrPrefix } from "@/components/site/DoctorNameInput";
import { toE164, isValid10Digit, to10Digit } from "@/lib/phone";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    UserPlus, WhatsappLogo, Copy, CheckCircle, Stethoscope, User, Crown,
    PencilSimple, ShieldCheck, Factory, Briefcase, Headset, Star, Info,
} from "@phosphor-icons/react";

const ROLE_GROUPS = [
    {
        label: "Provider",
        roles: [{ id: "doctor", label: "Doctor", Icon: Stethoscope, tone: "primary" }],
    },
    {
        label: "Center of Excellence",
        roles: [
            { id: "planner",    label: "Treatment Planner",       Icon: PencilSimple, tone: "violet" },
            { id: "qa",         label: "Quality Assurance",       Icon: ShieldCheck,  tone: "cyan" },
            { id: "production", label: "Production Tech",         Icon: Factory,      tone: "amber" },
            { id: "sales",      label: "Sales & KAM",             Icon: Briefcase,    tone: "emerald" },
            { id: "support",    label: "Triage & Support",        Icon: Headset,      tone: "rose" },
            { id: "coe_lead",   label: "Operations Head",         Icon: Star,         tone: "violet" },
        ],
    },
    {
        label: "Other",
        roles: [
            { id: "patient", label: "Patient", Icon: User,  tone: "primary" },
            { id: "admin",   label: "Admin", Icon: Crown, tone: "amber" },
        ],
    },
];

const TONES = {
    primary: "bg-primary/10 text-primary border-primary/20 ring-primary",
    amber:   "bg-amber-50 text-amber-700 border-amber-200 ring-amber-300",
    rose:    "bg-rose-50 text-rose-700 border-rose-200 ring-rose-300",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200 ring-emerald-300",
    cyan:    "bg-cyan-50 text-cyan-700 border-cyan-200 ring-cyan-300",
    violet:  "bg-violet-50 text-violet-700 border-violet-200 ring-violet-300",
};


/**
 * InviteUserDialog — one-click admin invite flow.
 *
 * Modal shape:
 *   Step 1 — pick role, fill name + phone (+ clinic for doctors)
 *   Step 2 — success: shows the new user record + a paste-ready WhatsApp
 *            message with copy + WhatsApp deep-link buttons
 *
 * Endpoints:
 *   POST /api/admin/invite-user
 *   GET  /api/admin/invite-user/roles  (optional — we ship a static list)
 */
export function InviteUserDialog({ open, onClose, onInvited }) {
    const [step, setStep] = useState(1);
    const [role, setRole] = useState("doctor");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [clinicName, setClinicName] = useState("");
    const [city, setCity] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open) {
            setStep(1); setRole("doctor"); setName(""); setPhone(""); setEmail("");
            setClinicName(""); setCity(""); setResult(null); setCopied(false);
        }
    }, [open]);

    const submit = async () => {
        if (!name.trim()) { toast.error("Name is required"); return; }
        if (!isValid10Digit(phone)) {
            toast.error("Enter the invitee's 10-digit Indian mobile number");
            return;
        }
        setBusy(true);
        try {
            // For doctors, the input value is the bare name (no "Dr.") since
            // DoctorNameInput strips it. The backend will prefix "Dr." when
            // building the display name.
            const submittedName = name.trim();
            const { data } = await api.post("/admin/invite-user", {
                role,
                name: submittedName,
                phone: toE164(phone),
                email: email.trim() || undefined,
                clinic_name: clinicName.trim() || undefined,
                city: city.trim() || undefined,
            });
            setResult(data);
            setStep(2);
            onInvited?.(data.user);
            if (data.already_existed) {
                toast.info("Phone already on the list — re-using the existing account.");
            } else {
                toast.success(`${data.user.name} added to the whitelist.`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't invite — check the form and try again.");
        } finally { setBusy(false); }
    };

    const copyMessage = () => {
        if (!result?.invite_message) return;
        navigator.clipboard.writeText(result.invite_message);
        setCopied(true);
        toast.success("Invite message copied — paste it into WhatsApp / SMS / email.");
        setTimeout(() => setCopied(false), 2500);
    };

    // WhatsApp deep-link with pre-filled body, using the actual deployed URL
    const waUrl = useMemo(() => {
        if (!result?.user?.phone || !result?.invite_message) return null;
        const num = result.user.phone.replace(/[^\d]/g, "");
        return `https://wa.me/${num}?text=${encodeURIComponent(result.invite_message)}`;
    }, [result]);

    const selectedRole = useMemo(() => {
        for (const g of ROLE_GROUPS) {
            for (const r of g.roles) if (r.id === role) return r;
        }
        return null;
    }, [role]);

    if (!open) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="invite-user-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlus size={20} weight="fill" className="text-primary" />
                        Invite a user
                    </DialogTitle>
                    <DialogDescription>
                        {step === 1
                            ? "Whitelist a phone number so the person can sign in. We'll generate a ready-to-paste invitation message at the end."
                            : "Your invitee is whitelisted. Send them this message — they'll be able to sign in immediately."}
                    </DialogDescription>
                </DialogHeader>

                {step === 1 ? (
                    <div className="space-y-5">
                        {/* Role picker */}
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Role</Label>
                            <div className="space-y-3">
                                {ROLE_GROUPS.map((g) => (
                                    <div key={g.label}>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 mb-1.5">{g.label}</div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                            {g.roles.map((r) => {
                                                const t = TONES[r.tone];
                                                const active = role === r.id;
                                                return (
                                                    <button
                                                        key={r.id}
                                                        type="button"
                                                        onClick={() => setRole(r.id)}
                                                        data-testid={`invite-role-${r.id}`}
                                                        className={`rounded-lg border p-2.5 flex items-center gap-2 text-xs font-semibold transition ${t} ${active ? "ring-2" : "opacity-70 hover:opacity-100"}`}
                                                    >
                                                        <r.Icon size={14} weight="fill" />
                                                        {r.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Common fields */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="invite-name">Full name<span className="text-rose-600 ml-0.5">*</span></Label>
                                {role === "doctor" ? (
                                    <>
                                        <div className="mt-1">
                                            <DoctorNameInput
                                                id="invite-name"
                                                value={name}
                                                onChange={setName}
                                                placeholder="Riya Kapoor"
                                                testid="invite-name-input"
                                            />
                                        </div>
                                        <p className="text-[10px] text-muted-foreground mt-1">No need to type &quot;Dr.&quot; — we add it for you.</p>
                                    </>
                                ) : (
                                    <Input
                                        id="invite-name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Priya Sharma"
                                        data-testid="invite-name-input"
                                    />
                                )}
                            </div>
                            <div>
                                <Label htmlFor="invite-phone">Phone<span className="text-rose-600 ml-0.5">*</span></Label>
                                <div className="mt-1">
                                    <PhoneInput10
                                        id="invite-phone"
                                        value={phone}
                                        onChange={setPhone}
                                        inputClassName="font-mono"
                                        testid="invite-phone-input"
                                    />
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">Indian mobile — 10 digits, country code is auto-added.</p>
                            </div>
                            <div className="sm:col-span-2">
                                <Label htmlFor="invite-email">Email (optional)</Label>
                                <Input
                                    id="invite-email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="priya@example.com"
                                    type="email"
                                    data-testid="invite-email-input"
                                />
                            </div>
                            {role === "doctor" && (
                                <>
                                    <div>
                                        <Label htmlFor="invite-clinic">Clinic name</Label>
                                        <Input
                                            id="invite-clinic"
                                            value={clinicName}
                                            onChange={(e) => setClinicName(e.target.value)}
                                            placeholder="Bright Smiles, Bandra"
                                            data-testid="invite-clinic-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="invite-city">City</Label>
                                        <Input
                                            id="invite-city"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            placeholder="Mumbai"
                                            data-testid="invite-city-input"
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Heads-up */}
                        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 flex gap-2.5">
                            <Info size={14} weight="fill" className="text-blue-700 shrink-0 mt-0.5" />
                            <div className="text-xs text-blue-900/90">
                                The invitee will sign in with phone <span className="font-mono font-semibold">{phone ? `+91 ${phone}` : "+91…"}</span> and OTP <span className="font-mono font-semibold">123456</span>. We'll generate a paste-ready message you can send via WhatsApp.
                            </div>
                        </div>
                    </div>
                ) : (
                    /* SUCCESS — Step 2 */
                    <div className="space-y-5">
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex gap-3 items-start">
                            <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0" />
                            <div>
                                <div className="font-display font-bold text-emerald-900">
                                    {result.already_existed ? "Re-issued invitation" : "Whitelisted"}
                                </div>
                                <div className="text-xs text-emerald-900/85 mt-0.5">
                                    {result.user.name} ({result.user.phone}) can now sign in
                                    {selectedRole ? <> as <span className="font-semibold">{selectedRole.label}</span></> : ""}.
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                                Paste-ready invitation
                            </Label>
                            <pre className="rounded-xl border border-border bg-muted/30 p-4 text-xs whitespace-pre-wrap font-sans leading-relaxed" data-testid="invite-message-preview">
                                {result.invite_message}
                            </pre>
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 flex-wrap sm:gap-2">
                    {step === 1 ? (
                        <>
                            <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
                            <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="invite-submit-btn">
                                <UserPlus size={14} weight="fill" className="mr-1.5" />
                                {busy ? "Whitelisting…" : "Whitelist & generate invite"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setStep(1)} className="rounded-full" data-testid="invite-another-btn">
                                Invite another
                            </Button>
                            <Button variant="outline" onClick={copyMessage} className="rounded-full" data-testid="invite-copy-btn">
                                {copied ? <CheckCircle size={13} weight="fill" className="mr-1.5 text-emerald-600" /> : <Copy size={13} className="mr-1.5" />}
                                {copied ? "Copied" : "Copy message"}
                            </Button>
                            {waUrl && (
                                <a
                                    href={waUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 h-9 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                                    data-testid="invite-whatsapp-btn"
                                >
                                    <WhatsappLogo size={13} weight="fill" /> Send via WhatsApp
                                </a>
                            )}
                            <Button onClick={onClose} className="rounded-full" data-testid="invite-done-btn">Done</Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default InviteUserDialog;
